import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { currencyFormat } from '@fiverr-private/localization';
import { Box } from '@fiverr-private/theme';
import BoldBodyInfoCard from './BoldBodyInfoCard';
import { ComplianceType } from './types';

interface InfoGridProps {
    timeEstimation: string;
    monthlyCredit: string;
    price: number;
    currency: string;
    requeseteeUserName: string;
    discountInPercents?: number;
    complianceType: ComplianceType;
}

interface ServiceCostProps {
    beforeDiscount: number;
    afterDiscount: number;
    currency: string;
}

const InfoGrid: React.FC<InfoGridProps> = ({
    timeEstimation,
    monthlyCredit,
    price,
    currency,
    requeseteeUserName,
    discountInPercents,
    complianceType,
}: {
    timeEstimation: string;
    monthlyCredit?: string;
    discountInPercents?: number;
    price: number;
    currency: string;
    complianceType: ComplianceType;
    requeseteeUserName: string;
}) => {
    const copyPrefix = 'compliance.info_grid';

    const freelancerTitle = translate(`${copyPrefix}.freelancer`);
    const timeEstimationTitle = translate(`${copyPrefix}.time_estimate`);
    const monthlyCreditTitle = translate(`${copyPrefix}.monthly_credit`);
    const checks = translate(`${copyPrefix}.${complianceType}.body`);
    const monthlyCreditBody = monthlyCredit && `${monthlyCredit} ${checks}`;
    const discountPrice = monthlyCredit && discountInPercents ? price - (discountInPercents * price) / 100 : price;
    return (
        <Stack
            direction="row"
            gap={{ default: '6', sm: '10' }}
            wrap={{ default: 'wrap', sm: 'nowrap' }}
            justifyContent="stretch"
        >
            <Stack gap="6" minWidth={'170px'} width="100%" justifyContent="flexStart">
                <BoldBodyInfoCard title={freelancerTitle} body={requeseteeUserName} complianceType={complianceType} />
                <BoldBodyInfoCard title={timeEstimationTitle} body={timeEstimation} complianceType={complianceType} />
            </Stack>
            <Stack gap="6" width="100%" minWidth={'170px'} justifyContent="flexStart">
                {monthlyCreditBody && (
                    <BoldBodyInfoCard
                        title={monthlyCreditTitle}
                        body={monthlyCreditBody}
                        monthlyCredit={monthlyCredit}
                        info={true}
                        complianceType={complianceType}
                    />
                )}
                <ServiceCost beforeDiscount={price} afterDiscount={discountPrice} currency={currency} />
            </Stack>
        </Stack>
    );
};

const ServiceCost: React.FC<ServiceCostProps> = ({ beforeDiscount, afterDiscount, currency }) => {
    const isWithDiscount = beforeDiscount !== afterDiscount;
    const formatBeforeDiscount = currencyFormat({
        value: beforeDiscount,
        currencyCode: currency,
    });
    const formatAfterDiscount = currencyFormat({
        value: afterDiscount,
        currencyCode: currency,
    });
    const priceBeforeDiscount = (
        <Text fontWeight={'semibold'} size={'b_md'}>
            {formatAfterDiscount}
        </Text>
    );
    return (
        <Stack gap={'2'} direction={'column'} justifyContent={'spaceBetween'}>
            <Text color={'bodySecondary'} size={'b_xs'}>
                <I18n k={'compliance.info_grid.price'} />
            </Text>
            <Stack gap={'2'}>
                {isWithDiscount ? (
                    <>
                        <Box color={'grey_600'}>
                            <Text color={'inherit'} size={'b_md'} decoration="lineThrough">
                                {formatBeforeDiscount}
                            </Text>
                        </Box>
                        {priceBeforeDiscount}
                    </>
                ) : (
                    priceBeforeDiscount
                )}
            </Stack>
        </Stack>
    );
};
export default InfoGrid;
